/**
 * Redux slice for admin account management: CRUD over accounts, lock/unlock,
 * nested user provisioning, and account switching (impersonation).
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { User } from '../types/auth';
import {
    Account,
    AccountsState,
    AccountCreate,
    AccountUpdate,
    AccountUserCreate,
    IMPERSONATED_ACCOUNT_KEY,
    IMPERSONATED_ACCOUNT_NAME_KEY,
    HOME_ACCOUNT_KEY,
} from '../types/account';
import accountsService from '../services/accountsService';
import authService from '../services/authService';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../api/client';
import { setCredentials } from './authSlice';
import { clearEvents, fetchEvents } from './eventsSlice';

// =============================================================================
// Initial State
// =============================================================================

const initialState: AccountsState = {
    accounts: [],
    total: 0,
    isLoading: false,
    error: null,
    mutatingIds: [],
    isSwitching: false,
    usersByAccount: {},
    loadingUsersFor: [],
    impersonatedAccountId: localStorage.getItem(IMPERSONATED_ACCOUNT_KEY),
    impersonatedAccountName: localStorage.getItem(IMPERSONATED_ACCOUNT_NAME_KEY),
    homeAccountId: localStorage.getItem(HOME_ACCOUNT_KEY),
};

// =============================================================================
// Error extraction (shared shape with clientsSlice)
// =============================================================================

function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: unknown } } };
        const detail = axiosError.response?.data?.detail;
        if (typeof detail === 'string') {
            return detail;
        }
        if (Array.isArray(detail)) {
            const messages = detail.map((err: { msg?: string; loc?: string[] }) => {
                const field = err.loc?.slice(-1)[0] || 'field';
                const msg = err.msg || 'validation error';
                return `${field}: ${msg}`;
            });
            return messages.join('; ') || 'Validation error';
        }
        if (detail && typeof detail === 'object' && 'message' in detail) {
            return String((detail as { message: unknown }).message);
        }
        return 'An error occurred';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An error occurred';
}

// =============================================================================
// Async Thunks
// =============================================================================

export const fetchAccounts = createAsyncThunk<
    { accounts: Account[]; total: number },
    void,
    { rejectValue: string }
>('accounts/fetchAccounts', async (_, { rejectWithValue }) => {
    try {
        const response = await accountsService.getAccounts();
        return { accounts: response.accounts, total: response.total };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const createAccount = createAsyncThunk<
    Account,
    AccountCreate,
    { rejectValue: string }
>('accounts/createAccount', async (data, { rejectWithValue }) => {
    try {
        return await accountsService.createAccount(data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const updateAccount = createAsyncThunk<
    Account,
    { accountId: string; data: AccountUpdate },
    { rejectValue: string }
>('accounts/updateAccount', async ({ accountId, data }, { rejectWithValue }) => {
    try {
        return await accountsService.updateAccount(accountId, data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const lockAccount = createAsyncThunk<
    Account,
    string,
    { rejectValue: string }
>('accounts/lockAccount', async (accountId, { rejectWithValue }) => {
    try {
        return await accountsService.lockAccount(accountId);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const unlockAccount = createAsyncThunk<
    Account,
    string,
    { rejectValue: string }
>('accounts/unlockAccount', async (accountId, { rejectWithValue }) => {
    try {
        return await accountsService.unlockAccount(accountId);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

/**
 * Switch the admin's effective account (impersonation). Pass null to revert to
 * the home account. Swaps the tokens (via setCredentials), re-fetches the
 * effective user, and reloads account-scoped data.
 */
export const switchAccount = createAsyncThunk<
    { impersonatedAccountId: string | null; impersonatedAccountName: string | null; homeAccountId: string },
    string | null,
    { rejectValue: string }
>('accounts/switchAccount', async (accountId, { dispatch, rejectWithValue }) => {
    try {
        const result = await accountsService.switchAccount(accountId);

        // Write the new tokens BEFORE fetching the user — apiClient reads the
        // access token from localStorage, so /auth/me must see the new token to
        // return the effective (impersonated) identity.
        localStorage.setItem(ACCESS_TOKEN_KEY, result.access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, result.refresh_token);
        const user = await authService.getCurrentUser();
        dispatch(
            setCredentials({
                user,
                accessToken: result.access_token,
                refreshToken: result.refresh_token,
            })
        );

        const impersonating =
            accountId !== null && accountId !== result.home_account_id;
        const impersonatedAccountId = impersonating ? result.account.id : null;
        const impersonatedAccountName = impersonating ? result.account.account_name : null;
        if (impersonatedAccountId) {
            localStorage.setItem(IMPERSONATED_ACCOUNT_KEY, impersonatedAccountId);
            localStorage.setItem(
                IMPERSONATED_ACCOUNT_NAME_KEY,
                impersonatedAccountName ?? ''
            );
        } else {
            localStorage.removeItem(IMPERSONATED_ACCOUNT_KEY);
            localStorage.removeItem(IMPERSONATED_ACCOUNT_NAME_KEY);
        }
        localStorage.setItem(HOME_ACCOUNT_KEY, result.home_account_id);

        // Reload account-scoped data for the new effective account.
        dispatch(clearEvents());
        dispatch(fetchEvents());

        return {
            impersonatedAccountId,
            impersonatedAccountName,
            homeAccountId: result.home_account_id,
        };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const fetchAccountUsers = createAsyncThunk<
    { accountId: string; users: User[] },
    string,
    { rejectValue: string }
>('accounts/fetchAccountUsers', async (accountId, { rejectWithValue }) => {
    try {
        const response = await accountsService.getUsers(accountId);
        return { accountId, users: response.users };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const createAccountUser = createAsyncThunk<
    { accountId: string; user: User },
    { accountId: string; data: AccountUserCreate },
    { rejectValue: string }
>('accounts/createAccountUser', async ({ accountId, data }, { rejectWithValue }) => {
    try {
        const user = await accountsService.createUser(accountId, data);
        return { accountId, user };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Slice
// =============================================================================

function replaceAccount(state: AccountsState, account: Account): void {
    const index = state.accounts.findIndex((a) => a.id === account.id);
    if (index !== -1) {
        state.accounts[index] = account;
    }
}

export const accountsSlice = createSlice({
    name: 'accounts',
    initialState,
    reducers: {
        clearAccountsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch accounts
        builder
            .addCase(fetchAccounts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAccounts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.accounts = action.payload.accounts;
                state.total = action.payload.total;
            })
            .addCase(fetchAccounts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch accounts';
            });

        // Create account
        builder
            .addCase(createAccount.fulfilled, (state, action) => {
                state.accounts.unshift(action.payload);
                state.total += 1;
            })
            .addCase(createAccount.rejected, (state, action) => {
                state.error = action.payload || 'Failed to create account';
            });

        // Update account
        builder
            .addCase(updateAccount.fulfilled, (state, action) => {
                replaceAccount(state, action.payload);
            })
            .addCase(updateAccount.rejected, (state, action) => {
                state.error = action.payload || 'Failed to update account';
            });

        // Lock / unlock (per-row in-flight via mutatingIds)
        builder
            .addCase(lockAccount.pending, (state, action) => {
                state.mutatingIds.push(action.meta.arg);
            })
            .addCase(lockAccount.fulfilled, (state, action) => {
                state.mutatingIds = state.mutatingIds.filter(
                    (id) => id !== action.payload.id
                );
                replaceAccount(state, action.payload);
            })
            .addCase(lockAccount.rejected, (state, action) => {
                state.mutatingIds = state.mutatingIds.filter(
                    (id) => id !== action.meta.arg
                );
                state.error = action.payload || 'Failed to lock account';
            })
            .addCase(unlockAccount.pending, (state, action) => {
                state.mutatingIds.push(action.meta.arg);
            })
            .addCase(unlockAccount.fulfilled, (state, action) => {
                state.mutatingIds = state.mutatingIds.filter(
                    (id) => id !== action.payload.id
                );
                replaceAccount(state, action.payload);
            })
            .addCase(unlockAccount.rejected, (state, action) => {
                state.mutatingIds = state.mutatingIds.filter(
                    (id) => id !== action.meta.arg
                );
                state.error = action.payload || 'Failed to unlock account';
            });

        // Switch account (impersonation)
        builder
            .addCase(switchAccount.pending, (state) => {
                state.isSwitching = true;
                state.error = null;
            })
            .addCase(switchAccount.fulfilled, (state, action) => {
                state.isSwitching = false;
                state.impersonatedAccountId = action.payload.impersonatedAccountId;
                state.impersonatedAccountName = action.payload.impersonatedAccountName;
                state.homeAccountId = action.payload.homeAccountId;
            })
            .addCase(switchAccount.rejected, (state, action) => {
                state.isSwitching = false;
                state.error = action.payload || 'Failed to switch account';
            });

        // Fetch account users
        builder
            .addCase(fetchAccountUsers.pending, (state, action) => {
                if (!state.loadingUsersFor.includes(action.meta.arg)) {
                    state.loadingUsersFor.push(action.meta.arg);
                }
            })
            .addCase(fetchAccountUsers.fulfilled, (state, action) => {
                state.loadingUsersFor = state.loadingUsersFor.filter(
                    (id) => id !== action.payload.accountId
                );
                state.usersByAccount[action.payload.accountId] = action.payload.users;
            })
            .addCase(fetchAccountUsers.rejected, (state, action) => {
                state.loadingUsersFor = state.loadingUsersFor.filter(
                    (id) => id !== action.meta.arg
                );
                state.error = action.payload || 'Failed to fetch users';
            });

        // Create account user
        builder
            .addCase(createAccountUser.fulfilled, (state, action) => {
                const { accountId, user } = action.payload;
                const existing = state.usersByAccount[accountId] || [];
                state.usersByAccount[accountId] = [...existing, user];
            })
            .addCase(createAccountUser.rejected, (state, action) => {
                state.error = action.payload || 'Failed to create user';
            });
    },
});

export const { clearAccountsError } = accountsSlice.actions;

export default accountsSlice.reducer;
